import type { Page } from '../../types';
import { CURRENT_REPORTING_YEAR_TOKEN } from '../../../utils/reporting-period';

const getReportYearOptions = (currentYear: number) =>
  Array.from({ length: 4 }, (_, idx) => {
    const year = currentYear - 2 + idx;
    const next = String(year + 1).slice(-2);
    return `${year}-${next}`;
  });

const reportYearOptions = getReportYearOptions(new Date().getFullYear());

const coverSheet: Page[] = [
  {
    pageType: 'question',
    title: 'Personal Details',
    name: 'person-details',
    hidePageTitle: true,
    questions: [
      {
        questionType: 'select',
        title: 'Period of Report',
        name: 'report-period',
        hint:
          'If completing your end-of-year record, select 2025-26. If creating your start-of-year record for this year, select 2026-27.',
        hintOption: 'Select an option',
        options: reportYearOptions,
        defaultValue: CURRENT_REPORTING_YEAR_TOKEN,
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'SCS Name',
        name: 'person-details-scs-name',
        hint: 'Please enter your full name',
        placeholder: 'Firstname Lastname',
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'SCS Email address',
        name: 'person-details-scs-email-address',
        hint: 'Please enter your Cabinet Office email address',
        placeholder: 'name@domain.com',
        userEmail: true,
        rules: {
          required: true,
          email: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'Official Job title',
        name: 'person-details-job-title',
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textInput',
        title: 'SOP Employee Number',
        name: 'employee-number',
        hint: 'Numbers only, e.g. cab123456 would be entered as 123456',
        placeholder: '123456',
        rules: {
          required: false,
          number: true,
        },
      },
      {
        questionType: 'select',
        title: 'Substantive Grade',
        name: 'person-details-scs-grade',
        hintOption: 'Select an option',
        options: [
          'SCS 1',
          'SCS 2',
          'SCS 3',
          'SCS 4',
          {
            option: 'Temporary promotion',
            questions: [
              {
                questionType: 'textInput',
                title: 'Please enter start and end date',
                name: 'person-details-scs-grade-temporary-promotion-dates',
                rules: {
                  required: true,
                },
              },
            ],
          },
        ],
        rules: {
          required: true,
        },
      },
      {
        questionType: 'radio',
        title: 'Temporary promotion',
        hint: 'Are you currently on temporary promotion?',
        name: 'temporary-promotion',
        options: [
          {
            option: 'Yes',
            questions: [
              {
                questionType: 'select',
                title: 'Please select your temporary grade',
                name: 'temporary-grade',
                hintOption: 'Select an option',
                options: ['SCS 1', 'SCS 2', 'SCS 3', 'SCS 4'],
                rules: {
                  required: true,
                },
              },
            ],
          },
          'No',
        ],
        rules: {
          required: true,
        },
      },
      {
        questionType: 'content',
        title: '',
        name: 'person-details-scs-scope-guidance',
        description:
          'Please refer to <a href="https://intranet.cabinetoffice.gov.uk/documents/2602/2025-12-23_SCS_End_of_Year_2025-26__Whos_In_scope.docx" target="_blank" rel="noopener noreferrer">SCS 25/26 End of Year: Who\'s in Scope</a> to establish if you are in scope.',
      },
      {
        questionType: 'select',
        title: 'Office Location',
        name: 'person-details-office-location',
        hintOption: 'Select an option',
        options: [
          'Basingstoke - Priestley House',
          'Belfast - Erskine House',
          'Birmingham - 23 Stephenson Street',
          'Bristol - 3 Glass Wharf',
          'Cardiff - Ty William Morgan',
          'Edinburgh - Queen Elizabeth House',
          'Glasgow - 1 Atlantic Square',
          'Leeds 7-8 Wellington Place',
          'Liverpool - The Capital Building',
          'London - 10 South Colonnade',
          'London - 10 Victoria Street',
          'London - 70 Whitehall',
          'London - 1 Horse Guards Road',
          'London - 100 Parliament Street',
          'London - 35 Great Smith Street',
          'London - Admiralty House',
          "Manchester - 2 St Peter's Square",
          'Milton Keynes - Hanslope Park',
          'Newcastle - Benton Park View',
          'Norwich - Rosebery Court',
          'Home worker',
          {
            option: 'Other',
            questions: [
              {
                questionType: 'textInput',
                title: 'Please specify',
                name: 'person-details-office-location-other',
                rules: { required: true },
              },
            ],
          },
          'Sheffield - Kings Court',
          'York - Foss House',
          'York - Imphal Barracks',
        ],
        rules: {
          required: true,
        },
      },
      {
        questionType: 'select',
        title: 'Your Primary Profession',
        name: 'person-details-profession-primary',
        detailsHeading: 'More information',
        detailsContent:
          'If you are unsure about which profession you are in, please visit <a href="https://www.gov.uk/government/publications/government-professions/government-professions" target="_blank" rel="noopener noreferrer">GOV.UK Guidance on Government Professions</a>.',
        hintOption: 'Select an option',
        options: [
          'Operational delivery',
          'Policy',
          {
            option: 'Functional professions',
            questions: [
              {
                questionType: 'select',
                title: 'Functional profession',
                name: 'person-details-profession-primary-functional',
                hintOption: 'Select a functional profession',
                options: [
                  'Actuary',
                  'Commercial',
                  'Communications',
                  'Counter Fraud',
                  'Debt',
                  'Digital and Data',
                  'Economics',
                  'Finance',
                  'Geography',
                  'Grants',
                  'Human Resources',
                  'Internal Audit',
                  'Legal',
                  'Project Delivery',
                  'Property',
                  'Operational Research',
                  'Risk Management',
                  'Security',
                  'Social research',
                  'Statistics',
                ],
                rules: { required: true },
              },
            ],
          },
          {
            option: 'Specialist professions',
            questions: [
              {
                questionType: 'select',
                title: 'Specialist profession',
                name: 'person-details-profession-primary-specialist',
                detailsHeading: 'More information',
                hintOption: 'Select a specialist profession',
                options: [
                  'Corporate Finance',
                  'Intelligence Analysis',
                  'Knowledge and Information Management',
                  'Clinical',
                  'Occupational Psychology',
                  'Planning',
                  'Planning Inspection',
                  'Science and Engineering',
                  'Tax',
                  'Veterinary',
                ],
                rules: { required: true },
              },
            ],
          },
        ],
        rules: {
          required: true,
        },
      },
      {
        questionType: 'select',
        title: 'Your Secondary Profession',
        name: 'person-details-profession-secondary',
        hintOption: 'Select an option',
        options: [
          'Operational delivery',
          'Policy',
          {
            option: 'Functional professions',
            questions: [
              {
                questionType: 'select',
                title: 'Functional profession',
                name: 'person-details-profession-secondary-functional',
                hintOption: 'Select a functional profession',
                options: [
                  'Actuary',
                  'Commercial',
                  'Communications',
                  'Counter Fraud',
                  'Debt',
                  'Digital and Data',
                  'Economics',
                  'Finance',
                  'Geography',
                  'Grants',
                  'Human Resources',
                  'Internal Audit',
                  'Legal',
                  'Project Delivery',
                  'Property',
                  'Operational Research',
                  'Risk Management',
                  'Security',
                  'Social research',
                  'Statistics',
                ],
                rules: { required: true },
              },
            ],
          },
          {
            option: 'Specialist professions',
            questions: [
              {
                questionType: 'select',
                title: 'Specialist profession',
                name: 'person-details-profession-secondary-specialist',
                hintOption: 'Select a specialist profession',
                options: [
                  'Corporate Finance',
                  'Intelligence Analysis',
                  'Knowledge and Information Management',
                  'Clinical',
                  'Occupational Psychology',
                  'Planning',
                  'Planning Inspection',
                  'Science and Engineering',
                  'Tax',
                  'Veterinary',
                ],
                rules: { required: true },
              },
            ],
          },
        ],
        rules: {
          required: false,
        },
      },
      {
        questionType: 'textInput',
        title: 'Manager Email',
        name: 'person-details-manager-email',
        approver: true,
        hint: 'Please list your line manager / the person expected to conduct your performance review',
        placeholder: 'name@domain.com',
        rules: {
          required: true,
          email: true,
        },
      },
      {
        questionType: 'autoComplete',
        title: 'Business Unit',
        hint: 'If you cannot find your business area, please enter: Other / Not listed',
        name: 'business-area',
        items: [
          'AI Taskforce*',
          'AI Security Institute*',
          'CO Digital',
          'CO People and Places',
          'COF Directorate',
          'Cost of Living taskforce*',
          'Covid-19 Inquiry',
          'Civil Service Strategy Unit',
          'Digital ID*',
          'Economic and Domestic Secretariat',
          'European and Global Issues Secretariat',
          'Future Civil Service*',
          'Government Commercial & Grants Function',
          'Government Communication Service',
          'Government in Parliament',
          'Government Property Agency',
          'Government Security Group',
          'Government Skills Group*',
          'Infected Blood Compensation Authority',
          'Intelligence Security Committee',
          'Joint Intelligence Organisation',
          'Ministers Accountability and Strategy Directorate*',
          'National Security Secretariat',
          'Office for Equality and Opportunity',
          'Office of Government Property',
          'Pensions & Operations Group*',
          'Place Regeneration taskforce*',
          "Prime Minister's Office",
          'Propriety and Constitution Group',
          'Public Control taskforce*',
          'Public Inquiry Response Unit',
          'Public Sector Fraud Authority',
          'UKSV',
          'Other / Not listed',
        ],
        options: [
          {
            option: 'Other / Not listed',
            questions: [
              {
                questionType: 'textInput',
                title: 'Please specify',
                name: 'person-details-business-unit-other',
                rules: {
                  required: true,
                },
              },
            ],
          },
        ],
        rules: {
          required: true,
        },
      },
    ],
  },
];

export default coverSheet;
